import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  WebSocketManager,
  ConnectionState,
  type ConnectionStatus,
  type WebSocketMessage,
} from "../services/WebSocketManager";
import { useAuth } from "./AuthContext";
import {
  appendDeploymentRoutingQuery,
  DEPLOYMENT_ROUTING_CHANGED_EVENT,
} from "@/utils/deploymentRouting";
import { canAttemptWebSocketAuth, getBearerTokenFromAuthData } from "@/utils/wsAuth";

interface WebSocketContextValue {
  status: Map<string, ConnectionStatus>;
  connect: (channel?: string) => void;
  disconnect: (channel?: string) => void;
  send: (channel: string, message: { type: string; payload: any }) => void;
  lastMessage: Map<string, WebSocketMessage | null>;
  ping: (channel: string) => void;
  subscribe: (channel: string) => void;
  unsubscribe: (channel: string) => void;
  onMessage: (
    channel: string,
    listener: (message: WebSocketMessage) => void
  ) => () => void;
  /** Fully tear down a channel (override linger). */
  closeChannel: (channel: string) => void;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

export interface WebSocketProviderProps {
  children: React.ReactNode;
  url: string;
  channels?: string[];
  autoConnect?: boolean;
  debug?: boolean;
  reconnectDelayMs?: number;
  maxReconnectAttempts?: number;
  pingIntervalMs?: number;
}

// Helper function to conditionally log based on localStorage.wsDebug
const conditionalLog = (...args: any[]) => {
  // Check if we're in a browser environment
  if (typeof window !== "undefined" && typeof localStorage !== "undefined") {
    // Only log if wsDebug is set to true in localStorage
    // if (localStorage.getItem("wsDebug") === "true") {
    //   console.log(...args);
    // }
  }
};

// Helper function to check if debug should be enabled
const shouldEnableDebug = (): boolean => {
  if (typeof window !== "undefined" && typeof localStorage !== "undefined") {
    return localStorage.getItem("wsDebug") === "true";
  }
  return false;
};

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
  url,
  channels = [url],
  autoConnect = true,
  debug = false,
  reconnectDelayMs = 2000,
  maxReconnectAttempts = 5,
  pingIntervalMs = 20000,
}) => {
  const [status, setStatus] = useState<Map<string, ConnectionStatus>>(
    new Map()
  );
  const [lastMessage, setLastMessage] = useState<
    Map<string, WebSocketMessage | null>
  >(new Map());
  /** Bumps when free-tier routing changes so connections use updated URLs */
  const [routingEpoch, setRoutingEpoch] = useState(0);
  const wsManager = useRef<WebSocketManager | null>(null);
  const isMounted = useRef(true);
  const pendingSubscriptions = useRef<Set<string>>(new Set());

  const effectiveUrl = useMemo(
    () => appendDeploymentRoutingQuery(url),
    [url, routingEpoch]
  );

  useEffect(() => {
    const onRoutingChange = () => setRoutingEpoch((e) => e + 1);
    window.addEventListener(DEPLOYMENT_ROUTING_CHANGED_EVENT, onRoutingChange);
    return () =>
      window.removeEventListener(
        DEPLOYMENT_ROUTING_CHANGED_EVENT,
        onRoutingChange
      );
  }, []);

  const { user, isAuthenticated, webSocketAuthReady, isLoading: authLoading } = useAuth();
  const clientId = user?.id ?? "client_id_1";

  // Use localStorage.wsDebug to determine if debugging should be enabled
  const wsDebug = shouldEnableDebug();

  // Initialize the WebSocket manager
  useEffect(() => {
    if (authLoading) return;

    conditionalLog("[WebSocketContext] Initializing with URL:", effectiveUrl);
    conditionalLog("[WebSocketContext] Using client ID:", clientId);

    // Bearer JWT for legacy email/password users; WorkOS users use session cookies.
    const authToken = getBearerTokenFromAuthData();

    // Create a new WebSocketManager
    const newWsManager = new WebSocketManager({
      url: effectiveUrl,
      reconnectDelayMs,
      maxReconnectAttempts, // 0 = no reconnect; default 5 via WebSocketManager
      debug: wsDebug,
      clientId,
      pingIntervalMs,
      authToken: authToken ?? undefined,
      disablePing: true,
    });

    // This effect can re-run under React StrictMode; clear per-manager attachment
    // tracking so listeners are re-attached for the fresh manager instance.
    channelEventListenersAttachedRef.current.clear();

    wsManager.current = newWsManager;
    isMounted.current = true;


    // Process pending subscriptions that were queued before initialization
    if (pendingSubscriptions.current.size > 0) {
      conditionalLog(`[WebSocketContext] Processing ${pendingSubscriptions.current.size} pending subscriptions`);
      const pending = Array.from(pendingSubscriptions.current);
      pendingSubscriptions.current.clear();
      
      // Process immediately instead of setTimeout
      pending.forEach(channel => {
        
        if (wsManager.current) {
          // Initialize channel status
          setStatus((prev) => {
            if (prev.has(channel)) return prev;
            return new Map(prev).set(channel, {
              state: ConnectionState.DISCONNECTED,
              lastConnected: null,
              lastError: null,
            });
          });
          setLastMessage((prev) => {
            if (prev.has(channel)) return prev;
            return new Map(prev).set(channel, null);
          });

          // Attach event listeners
          if (!channelEventListenersAttachedRef.current.has(channel)) {
            channelEventListenersAttachedRef.current.add(channel);

            wsManager.current.on(
              channel,
              "statusChange",
              (newStatus: ConnectionStatus) => {
                if (isMounted.current) {
                  conditionalLog(
                    `[WebSocketContext] Status changed for ${channel}:`,
                    newStatus.state
                  );
                  setStatus((prev) => new Map(prev).set(channel, newStatus));
                }
              }
            );

            wsManager.current.on(
              channel,
              "message",
              (message: WebSocketMessage) => {
                if (isMounted.current) {
                  conditionalLog(
                    `[WebSocketContext] Message received on ${channel}:`,
                    message.type
                  );
                  setLastMessage((prev) => new Map(prev).set(channel, message));
                }
              }
            );
          }

          // Now actually subscribe
          wsManager.current.subscribe(channel);
        }
      });
    }

    // Initialize status and lastMessage for main channel
    setStatus((prev) => {
      const newMap = new Map(prev);
      newMap.set(effectiveUrl, {
        state: ConnectionState.DISCONNECTED,
        lastConnected: null,
        lastError: null,
      });
      return newMap;
    });

    setLastMessage((prev) => {
      const newMap = new Map(prev);
      newMap.set(effectiveUrl, null);
      return newMap;
    });

    // Set up event listeners for main channel
    const setupEventListeners = () => {
      if (!wsManager.current) return;

      conditionalLog(
        "[WebSocketContext] Setting up event listeners for:",
        effectiveUrl
      );

      wsManager.current.on(
        effectiveUrl,
        "statusChange",
        (newStatus: ConnectionStatus) => {
          if (isMounted.current) {
            conditionalLog(
              `[WebSocketContext] Status changed for ${effectiveUrl}:`,
              newStatus.state
            );
            setStatus((prev) => {
              const newMap = new Map(prev);
              newMap.set(effectiveUrl, newStatus);
              return newMap;
            });
          }
        }
      );

      wsManager.current.on(effectiveUrl, "message", (message: WebSocketMessage) => {
        if (isMounted.current) {
          conditionalLog(
            `[WebSocketContext] Message received on ${effectiveUrl}:`,
            message.type
          );
          setLastMessage((prev) => {
            const newMap = new Map(prev);
            newMap.set(effectiveUrl, message);
            return newMap;
          });
        }
      });
    };

    // Set up the event listeners
    setupEventListeners();

    // Create a safe connect function that checks if wsManager still exists
    const safeConnect = () => {
      if (wsManager.current && isMounted.current) {
        conditionalLog("[WebSocketContext] Auto-connecting...");
        try {
          wsManager.current.connect();
        } catch (error) {
          conditionalLog(
            "[WebSocketContext] Error during auto-connect:",
            error
          );
        }
      }
    };

    // Auto-connect if enabled with a slight delay to ensure initialization is complete
    if (autoConnect && canAttemptWebSocketAuth(isAuthenticated, webSocketAuthReady)) {
      const connectTimer = setTimeout(() => {
        safeConnect();
      }, 100);

      // Clear the timer if component unmounts before connection is attempted
      return () => {
        clearTimeout(connectTimer);
        cleanupWebSocketManager();
      };
    }

    // Cleanup function to properly dispose of WebSocketManager
    function cleanupWebSocketManager() {
      conditionalLog("[WebSocketContext] Cleaning up WebSocketManager");
      isMounted.current = false;
      channelEventListenersAttachedRef.current.clear();

      // Need to access the current ref value at cleanup time
      const currentWsManager = wsManager.current;
      if (currentWsManager) {
        try {
          currentWsManager.removeAllListeners();
          if ((currentWsManager as any).destroy) {
            (currentWsManager as any).destroy();
          } else {
            currentWsManager.disconnect();
          }
        } catch (error) {
          conditionalLog("[WebSocketContext] Error during cleanup:", error);
        }
        wsManager.current = null;
      }
    }

    // Return cleanup function
    return cleanupWebSocketManager;
  }, [effectiveUrl, autoConnect, isAuthenticated, webSocketAuthReady, authLoading, clientId]);

  // Listener registry per-channel for stable delivery and ref-counting
  const channelListenerCountsRef = useRef<Map<string, number>>(new Map());
  const channelSubscriptionCountsRef = useRef<Map<string, number>>(new Map());
  const messageHandlersRef = useRef<
    Map<string, Set<(m: WebSocketMessage) => void>>
  >(new Map());
  const channelEventListenersAttachedRef = useRef<Set<string>>(new Set());

  const ensureChannelListener = useCallback((channel: string) => {
    if (!wsManager.current) return;

    const counts = channelListenerCountsRef.current;
    const currentCount = counts.get(channel) || 0;
    if (currentCount === 0) {
      // First logical subscription: attach a single manager listener to fan-out
      wsManager.current.on(channel, "message", (msg) => {
        const handlers = messageHandlersRef.current.get(channel);
        if (!handlers || handlers.size === 0) return;
        handlers.forEach((h) => {
          try {
            h(msg);
          } catch {
            // swallow listener errors
          }
        });
      });
    }
    counts.set(channel, currentCount + 1);
  }, []);

  const releaseChannelListener = useCallback((channel: string) => {
    const counts = channelListenerCountsRef.current;
    const currentCount = counts.get(channel) || 0;
    const next = Math.max(0, currentCount - 1);
    counts.set(channel, next);
    // We keep the underlying single manager listener attached; safe no-op when no handlers
  }, []);

  // onMessage will be declared after the ref-counting subscribe/unsubscribe below

  // Subscribe method
  const subscribe = useCallback((channel: string) => {
    if (!canAttemptWebSocketAuth(isAuthenticated, webSocketAuthReady)) {
      return;
    }

    const counts = channelSubscriptionCountsRef.current;
    const current = counts.get(channel) || 0;
    counts.set(channel, current + 1);

    if (current > 0) {
      // Already subscribed and listeners attached
      conditionalLog(
        `[WebSocketContext] Subscribing to ${channel} (ref++ -> ${current + 1})`
      );
      return;
    }
    conditionalLog(`[WebSocketContext] Subscribing to ${channel}`);

    if (!wsManager.current) {
      // Queue the subscription for later when wsManager is ready
      pendingSubscriptions.current.add(channel);
      return;
    }

    if (wsManager.current) {
      // Attach event listeners FIRST, before subscribing
      if (!channelEventListenersAttachedRef.current.has(channel)) {
        channelEventListenersAttachedRef.current.add(channel);

        wsManager.current.on(
          channel,
          "statusChange",
          (newStatus: ConnectionStatus) => {
            if (isMounted.current) {
              conditionalLog(
                `[WebSocketContext] Status changed for ${channel}:`,
                newStatus.state
              );
              setStatus((prev) => new Map(prev).set(channel, newStatus));
            }
          }
        );

        wsManager.current.on(
          channel,
          "message",
          (message: WebSocketMessage) => {
            if (isMounted.current) {
              conditionalLog(
                `[WebSocketContext] Message received on ${channel}:`,
                message.type
              );
              setLastMessage((prev) => new Map(prev).set(channel, message));
            }
          }
        );
      }

      // Initialize status and lastMessage for new channel (once)
      setStatus((prev) => {
        if (prev.has(channel)) return prev;
        return new Map(prev).set(channel, {
          state: ConnectionState.DISCONNECTED,
          lastConnected: null,
          lastError: null,
        });
      });
      setLastMessage((prev) => {
        if (prev.has(channel)) return prev;
        return new Map(prev).set(channel, null);
      });

      // Now subscribe - Only subscribe if not already connected/connecting
      const statusForChannel = wsManager.current.getStatus(channel);
      if (
        statusForChannel.state === ConnectionState.CONNECTED ||
        statusForChannel.state === ConnectionState.CONNECTING ||
        statusForChannel.state === ConnectionState.RECONNECTING
      ) {
        // already active
      } else {
        wsManager.current.subscribe(channel);
      }
    }
  }, [isAuthenticated, webSocketAuthReady]);

  // Unsubscribe method
  const unsubscribe = useCallback((channel: string) => {
    const counts = channelSubscriptionCountsRef.current;
    const current = counts.get(channel) || 0;
    if (current <= 1) {
      // Linger: keep the underlying socket connected to avoid churn
      counts.set(channel, 0);
      conditionalLog(`[WebSocketContext] Unsubscribe (linger) ${channel}`);
      // Do not call wsManager.unsubscribe here; leave connection alive
      // Also keep status/lastMessage entries;
    } else {
      counts.set(channel, current - 1);
      conditionalLog(
        `[WebSocketContext] Unsubscribe ${channel} (ref-- -> ${current - 1})`
      );
    }
  }, []);

  // Final onMessage: attach/detach handlers only (no implicit subscribe/unsubscribe)
  const onMessage = useCallback(
    (channel: string, listener: (message: WebSocketMessage) => void) => {
      ensureChannelListener(channel);

      const handlers = messageHandlersRef.current.get(channel) || new Set();
      handlers.add(listener);
      messageHandlersRef.current.set(channel, handlers);

      return () => {
        const setForChannel = messageHandlersRef.current.get(channel);
        if (setForChannel) {
          setForChannel.delete(listener);
          if (setForChannel.size === 0) {
            messageHandlersRef.current.delete(channel);
          }
        }
        releaseChannelListener(channel);
      };
    },
    [ensureChannelListener, releaseChannelListener]
  );

  // Connect method
  const connect = useCallback((channel?: string) => {
    conditionalLog(
      `[WebSocketContext] Connect called${channel ? ` for ${channel}` : ""}`
    );
    if (wsManager.current) {
      wsManager.current.connect(channel);
    }
  }, []);

  // Disconnect method
  const disconnect = useCallback((channel?: string) => {
    conditionalLog(
      `[WebSocketContext] Disconnect called${channel ? ` for ${channel}` : ""}`
    );
    if (wsManager.current) {
      wsManager.current.disconnect(channel);
    }
  }, []);

  // Send method
  const send = useCallback(
    (channel: string, message: { type: string; payload: any }) => {
      conditionalLog(
        `[WebSocketContext] Sending message on ${channel}:`,
        message.type
      );
      if (wsManager.current) {
        wsManager.current.send(channel, message);
      }
    },
    []
  );

  // Ping method
  const ping = useCallback((channel: string) => {
    conditionalLog(`[WebSocketContext] Manual ping requested for ${channel}`);
    if (wsManager.current) {
      wsManager.current.ping(channel);
    }
  }, []);

  // Force-close a channel and remove listeners/state (used to override linger)
  const closeChannel = useCallback((channel: string) => {
    try {
      // Reset ref counts
      channelSubscriptionCountsRef.current.set(channel, 0);
      channelListenerCountsRef.current.delete(channel);

      // Remove any app-level message handlers for this channel
      messageHandlersRef.current.delete(channel);
      channelEventListenersAttachedRef.current.delete(channel);

      // Remove manager listeners and close/unsubscribe the channel entirely
      if (wsManager.current) {
        try {
          (wsManager.current as any).removeAllListeners?.(channel);
        } catch { /* Silently ignore - channel may already be closed */ }
        try {
          (wsManager.current as any).unsubscribe?.(channel);
        } catch { /* Silently ignore - channel may already be closed */ }
        try {
          wsManager.current.disconnect(channel);
        } catch { /* Silently ignore - channel may already be closed */ }
      }

      // Prune status/lastMessage maps
      setStatus((prev) => {
        const next = new Map(prev);
        next.delete(channel);
        return next;
      });
      setLastMessage((prev) => {
        const next = new Map(prev);
        next.delete(channel);
        return next;
      });

      conditionalLog(`[WebSocketContext] Closed channel ${channel}`);
    } catch (e) {
      conditionalLog(`[WebSocketContext] Error closing channel ${channel}:`, e);
    }
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue: WebSocketContextValue = {
    status,
    connect,
    disconnect,
    send,
    lastMessage,
    ping,
    subscribe,
    unsubscribe,
    onMessage,
    closeChannel,
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

// Hook to use the WebSocket context
export const useWebSocket = () => {
  const context = useContext(WebSocketContext);

  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }

  return context;
};
